import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export type EventState = "locked" | "live" | "closed" | "no-event";

export interface ExhibitionEventData {
  id: string | null;
  name: string | null;
  description: string | null;
  starts_at: string | null;
  ends_at: string | null;
  is_live: boolean;
  countdown_seconds: number;
  event_state: EventState;
  admin_override: boolean;
  paused: boolean;
  display_title: string | null;
  display_message: string | null;
  is_published: boolean;
  debug_info: Record<string, unknown> | null;
}

export interface CountdownDisplay {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface TimeRemainingDisplay {
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Hook for consuming exhibition event state from the backend.
 * CRITICAL: All state logic is computed by the database function.
 * Frontend NEVER calculates event state - only displays what backend says.
 */
export const useExhibitionEventState = () => {
  const [eventData, setEventData] = useState<ExhibitionEventData | null>(null);
  const [countdown, setCountdown] = useState<CountdownDisplay | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<TimeRemainingDisplay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const countdownRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch event state from backend - SINGLE SOURCE OF TRUTH
  const fetchEventState = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase.rpc("get_current_exhibition_event");

      if (fetchError) {
        console.error("Error fetching exhibition event:", fetchError);
        setError(fetchError.message);
        return;
      }

      if (data && data.length > 0) {
        const event = data[0] as {
          id: string;
          name: string;
          description: string;
          starts_at: string;
          ends_at: string;
          is_live: boolean;
          countdown_seconds: number;
          event_state: string;
          admin_override: boolean;
          paused: boolean;
          display_title: string | null;
          display_message: string | null;
          is_published: boolean;
          debug_info: Record<string, unknown>;
        };
        
        const newEventData: ExhibitionEventData = {
          id: event.id,
          name: event.name,
          description: event.description,
          starts_at: event.starts_at,
          ends_at: event.ends_at,
          is_live: event.is_live,
          countdown_seconds: event.countdown_seconds,
          event_state: event.event_state as EventState,
          admin_override: event.admin_override,
          paused: event.paused ?? false,
          display_title: event.display_title,
          display_message: event.display_message,
          is_published: event.is_published ?? false,
          debug_info: event.debug_info,
        };
        
        setEventData(newEventData);
        countdownRef.current = event.countdown_seconds;
        
        // Update displays immediately
        updateDisplays(newEventData.event_state, event.countdown_seconds);
      } else {
        setEventData({
          id: null,
          name: null,
          description: null,
          starts_at: null,
          ends_at: null,
          is_live: false,
          countdown_seconds: 0,
          event_state: "no-event",
          admin_override: false,
          paused: false,
          display_title: null,
          display_message: null,
          is_published: false,
          debug_info: null,
        });
        setCountdown(null);
        setTimeRemaining(null);
      }
      setError(null);
    } catch (err) {
      console.error("Error in fetchEventState:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Format countdown from seconds
  const formatCountdown = (seconds: number): CountdownDisplay => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return { days, hours, minutes, seconds: secs };
  };

  // Format time remaining for live events
  const formatTimeRemaining = (seconds: number): TimeRemainingDisplay => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return { hours, minutes, seconds: secs };
  };

  // Update display based on state
  const updateDisplays = (state: EventState, seconds: number) => {
    if (state === "locked" && seconds > 0) {
      setCountdown(formatCountdown(seconds));
      setTimeRemaining(null);
    } else if (state === "live" && seconds > 0) {
      setCountdown(null);
      setTimeRemaining(formatTimeRemaining(seconds));
    } else {
      setCountdown(null);
      setTimeRemaining(null);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchEventState();
  }, [fetchEventState]);

  // Local countdown timer - ticks every second but only updates UI
  useEffect(() => {
    if (!eventData) return;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    countdownRef.current = eventData.countdown_seconds;

    if (countdownRef.current <= 0) return;

    intervalRef.current = setInterval(() => {
      countdownRef.current -= 1;
      
      if (countdownRef.current <= 0) {
        // Refresh from backend when countdown reaches zero
        fetchEventState();
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        return;
      }

      // Update local display only
      updateDisplays(eventData.event_state, countdownRef.current);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [eventData?.event_state, eventData?.countdown_seconds, fetchEventState]);

  // Poll backend every 15 seconds to sync state
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchEventState();
    }, 15 * 1000);

    return () => clearInterval(refreshInterval);
  }, [fetchEventState]);

  return {
    // Event data
    eventData,
    eventState: eventData?.event_state ?? "no-event",
    isLive: eventData?.is_live ?? false,
    isAdminOverride: eventData?.admin_override ?? false,
    isPaused: eventData?.paused ?? false,
    isPublished: eventData?.is_published ?? false,
    
    // Schedule info
    startsAt: eventData?.starts_at ? new Date(eventData.starts_at) : null,
    endsAt: eventData?.ends_at ? new Date(eventData.ends_at) : null,
    
    // Display content (announcement)
    displayTitle: eventData?.display_title,
    displayMessage: eventData?.display_message,
    
    // Display values (pre-formatted)
    countdown,
    timeRemaining,
    
    // Raw countdown seconds for custom formatting
    countdownSeconds: countdownRef.current,
    
    // Debug info (for admin panel)
    debugInfo: eventData?.debug_info,
    
    // State
    loading,
    error,
    
    // Actions
    refresh: fetchEventState,
  };
};
